import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  LocalStoreService,
  NotifyService,
  PreloaderService,
} from '@core/services';
import { DepartmentContextService } from '@core/services/department-context.service';
import { environment } from '@environment/environment';
import { OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, from, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  finalize,
  filter,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  Constants,
  HttpErrorCode,
  LocalStorageKeys,
  Notifications,
  NotificationsHeading,
  ToastTypes,
} from '../../configs/app.config';

@Injectable()
export class CustomInterceptor implements HttpInterceptor {
  errorMessageKey = 'title';
  errorMessage = 'PAGE.TOAST_MESSAGE.ERROR';
  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(
    null,
  );

  /* istanbul ignore next */
  constructor(
    private readonly localStoreService: LocalStoreService,
    private readonly notify: NotifyService,
    private readonly preloader: PreloaderService,
    private readonly appConstants: Constants,
    private readonly oauthService: OAuthService,
    private readonly departmentContext: DepartmentContextService,
    private readonly router: Router,
  ) {}

  /**
   * Method to intercept the http requests.
   *
   * @param req
   * @param next
   * @returns intercepted request.
   */
  /* istanbul ignore next */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const config = JSON.parse(req.params.get('_config') ?? '{}');

    return next.handle(this.setRequestHeader(req, config)).pipe(
      tap((event) => this.stopPreloaderForResponse(event, config)),
      catchError((err) => {
        if (!config.noLoader) {
          this.preloader.stop();
        }
        if (err instanceof HttpErrorResponse) {
          if (err.status === HttpErrorCode.UNAUTHORIZED) {
            // Attempt to refresh token on 401 before showing error
            return this.handleUnauthorizedError(err, req, next, config);
          } else if (err.status === HttpErrorCode.FORBIDDEN) {
            this.localStoreService.clear();
          }
          return this.onSubscribeError(err, config);
        } else {
          return of(err);
        }
      }),
      finalize(() => {}),
    );
  }

  /**
   * Method to set the request heaader.
   *
   * @param req
   * @param config
   * @returns request body
   */
  /* istanbul ignore next */
  setRequestHeader(req: HttpRequest<any>, config: any): any {
    this.requestInterceptor(config.noLoader);
    if (
      req.url.includes('assets/i18n') ||
      req.url.includes('https://') ||
      req.url.includes('http://') ||
      req.url.includes('assets/sheets/') ||
      req.url.includes('assets/config/')
    ) {
      return req;
    } else {
      const accessToken = this.oauthService.getAccessToken();
      const existingToken = this.localStoreService.get(
        LocalStorageKeys.AccessToken,
      );
      if (accessToken !== existingToken) {
        this.oauthService.logOut();
      }
      req = req.clone({ params: req.params.delete('_config') });
      req = req.clone({
        url: `${environment.APIBasePath}${this.removeSlashes(req.url)}`,
      });

      // Set content type
      if (!config.bypassContentType) {
        req = req.clone({
          headers: req.headers.set('Content-Type', 'application/json'),
        });
      }
      if (config.urlEncodedContentType) {
        req = req.clone({
          headers: req.headers.set(
            'Content-Type',
            'application/x-www-form-urlencoded;charset=UTF-8',
          ),
        });
      }

      // Set Accept Type
      req = req.clone({
        headers: req.headers.set('Accept', 'application/json'),
      });

      // Add department ID header (except for login endpoint)
      const activeDepartment = this.departmentContext.getActiveDepartmentId();
      if (activeDepartment && !req.url.includes('login')) {
        req = req.clone({
          headers: req.headers.set('Department-Id', activeDepartment),
        });
      }

      // Set bearer token in Authorization header
      if (accessToken) {
        req = req.clone({
          headers: req.headers.set('authorization', `Bearer ${accessToken}`),
        });
      }
      return req;
    }
  }

  /**
   * Method to remove heading and tailing slashes.
   *
   * @param url
   * @returns url with removed slashes.
   */
  /* istanbul ignore next */
  private readonly removeSlashes = (url: string): string => {
    if (url && url.startsWith('/')) {
      url = url.slice(1, url.length);
    } else if (url && url.endsWith('/')) {
      url = url.slice(0, url.length - 1);
    }
    return url;
  };

  /**
   * Method to start the preloader.
   *
   * @param noLoader
   */
  /* istanbul ignore next */
  private requestInterceptor(noLoader: any): void {
    if (!noLoader) {
      this.preloader.start();
    }
  }

  /**
   * Method to stop preloader.
   *
   * @param event
   * @param config
   */
  /* istanbul ignore next */
  private stopPreloaderForResponse(event: HttpEvent<any>, config: any): void {
    if (event instanceof HttpResponse && !config.noLoader) {
      this.preloader.stop();
    }
  }

  /**
   * Method to handle HTTP errors
   *
   * @param error
   * @param config
   */
  /* istanbul ignore next */
  private onSubscribeError(
    error: any,
    config: any,
  ): Observable<HttpEvent<any>> {
    if (error.status === 0 && !error.ok) {
      // Check internet status
      if (!navigator.onLine) {
        this.notify.show(
          ToastTypes.Error,
          NotificationsHeading.ERROR,
          Notifications.OFFLINE,
        );
        return throwError(() => new Error(error));
      } else {
        this.notify.show(
          ToastTypes.Error,
          NotificationsHeading.ERROR,
          this.appConstants.commonAPIErrorMessage,
        );
        return throwError(() => new Error(error));
      }
    } else if (error.status === HttpErrorCode.UNAUTHORIZED) {
      // Handle 401 unauthorized errors.
      this.notify.show(
        ToastTypes.Error,
        NotificationsHeading.SESSION_EXPIRED,
        Notifications.SESSION_EXPIRED,
      );
      this.localStoreService.clear();
      setTimeout(() => {
        this.oauthService.initCodeFlow(undefined, { prompt: 'login' });
      }, 1500);
    } else if (error.status === HttpErrorCode.UNPROCESSABLE_ENTITY) {
      // Handle 422 request errors.
      this.handle422Error(error.error.errors);
    } else {
      if (!config.noNotifyError) {
        // Show server error messages.
        if (error.error && error.error[this.errorMessageKey]) {
          this.notify.show(
            ToastTypes.Error,
            NotificationsHeading.ERROR,
            error.error[this.errorMessageKey],
          );
        } else if (error.error?.isLoggedIn) {
          this.notify.show(
            ToastTypes.Error,
            NotificationsHeading.ERROR,
            this.appConstants.commonAPIErrorMessage,
          );
        }
      }
    }
    return throwError(() => new Error(error));
  }

  /**
   * Method to handle 401 Unauthorized errors with token refresh retry.
   * Attempts to refresh the access token and retry the request.
   * If refresh fails, clears storage and shows error.
   *
   * @param error
   * @param req
   * @param next
   * @param config
   */
  /* istanbul ignore next */
  private handleUnauthorizedError(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler,
    config: any,
  ): Observable<HttpEvent<any>> {
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap(() =>
          next.handle(this.setRequestHeader(req, config)).pipe(
            tap((event) => this.stopPreloaderForResponse(event, config)),
            catchError((retryErr) => this.handleRetryError(retryErr, config)),
          ),
        ),
        catchError(() => this.handleRetryError(error, config)),
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.refreshAccessToken().pipe(
      switchMap((newToken) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(newToken);
        return next.handle(this.setRequestHeader(req, config)).pipe(
          tap((event) => this.stopPreloaderForResponse(event, config)),
          catchError((retryErr) => {
            if (!config.noLoader) {
              this.preloader.stop();
            }
            return this.onSubscribeError(retryErr, config);
          }),
        );
      }),
      catchError((refreshError) => {
        this.isRefreshing = false;
        this.notify.show(
          ToastTypes.Error,
          NotificationsHeading.SESSION_EXPIRED,
          Notifications.SESSION_EXPIRED,
        );
        this.refreshTokenSubject.error(refreshError);
        this.localStoreService.clear();
        setTimeout(() => {
          this.oauthService.initCodeFlow(undefined, { prompt: 'login' });
        }, 1500);
        return throwError(() => new Error(refreshError));
      }),
    );
  }

  private handleRetryError(
    error: any,
    config: any,
  ): Observable<HttpEvent<any>> {
    if (!config.noLoader) {
      this.preloader.stop();
    }
    return this.onSubscribeError(error, config);
  }

  /**
   * Method to refresh the access token using OAuth service.
   * Uses the refresh token to obtain a new access token.
   *
   * @returns Observable that completes when token is refreshed
   */
  /* istanbul ignore next */
  private refreshAccessToken(): Observable<string> {
    return from(this.oauthService.refreshToken()).pipe(
      switchMap(() => {
        const newToken = this.oauthService.getAccessToken();
        if (!newToken) {
          return throwError(() => new Error('Token refresh failed'));
        }
        this.localStoreService.store(LocalStorageKeys.AccessToken, newToken);
        return of(newToken);
      }),
    );
  }

  /**
   * Method to handle 422 HTTP errors.
   *
   * @param error
   */
  /* istanbul ignore next */
  private handle422Error(error: any): void {
    Object.keys(error).forEach((e) => {
      this.notify.show(
        ToastTypes.Error,
        NotificationsHeading.ERROR,
        error[e][0],
      );
    });
  }
}
